<template>
  <ToggleCard
    v-model="model"
    v-bind="{ disabled, name, variant }"
    container-id="lottery_add_on_form"
    :info-button-aria-label="$t('infobutton.conveniencefee.label')"
    :info-headline="
      $t(`lottery.addontoggle.info.headline.${name}`, {
        addOn: translatedAddOn,
      })
    "
    :info-text="
      $t(`lottery.addontoggle.info.text.${name}`, {
        addOn: translatedAddOn,
      })
    "
    :switch-aria-label-text="$t('lottery.addontoggle.default.switch')"
  >
    <template #headline>{{ translatedAddOn }}</template>

    <div
      class="add-on-footer flex flex-grow items-center justify-between"
      data-testid="add-on-footer"
    >
      <span v-if="disabled" class="text-primary-primary" data-testid="add-on-restriction">
        {{ restrictionText }}
      </span>

      <template v-else>
        <span data-testid="add-on-hint">
          {{
            $t(`lottery.addontoggle.hint.${name}`, {
              addOn: translatedAddOn.toUpperCase(),
            })
          }}
        </span>

        <i18n-t
          class="paragraph-small-text py-050 px-100 rounded-s bg-secondary-secondary text-primary-primary text-right price-per-line"
          data-testid="add-on-price"
          :keypath="pricePerPlayKeyPath"
          scope="global"
          tag="span"
        >
          <template #amount>
            <strong class="paragraph-bold">{{ pricePerPlay }}</strong>
          </template>
        </i18n-t>
      </template>
    </div>
  </ToggleCard>
</template>

<script lang="ts" setup>
import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import { EDITOR_ADD_ON_VARIANTS, type EditorAddOnVariantName } from '../constants/optionCards'

type Props = {
  lotteryId: LotteryId
  name: string
}

const { lotteryId, name } = defineProps<Props>()

const { getAddOnPricePerPlay, store } = useEditorOptions(lotteryId)
const { t, te } = useI18n()

const model = computed({
  get: () => store.isAddOnSelected(name),
  set: (isActive) => store.setAddOn(name, isActive),
})

const disabled = computed(() => store.isAddOnDisabled(name))

const requiredAddOn = computed(() => store.getAddOnRequirement(name))

const restrictionText = computed(() =>
  t('lottery.addontoggle.disabled.requiresaddon', {
    addOn: translatedAddOn.value.toUpperCase(),
    requiredAddOn: t(`lottery.addon.${requiredAddOn.value}`).toUpperCase(),
  }),
)

const pricePerPlay = computed(() => getAddOnPricePerPlay(name))

const pricePerPlayKeyPath = computed(() => {
  const key = `lottery.addontoggle.${name}.priceperplay`
  return te(key) ? key : 'lottery.addontoggle.default.priceperplay'
})

const translatedAddOn = computed(() => t(`lottery.addon.${name}`))

const variant = computed(() => {
  if (!hasVariant(name)) return
  return EDITOR_ADD_ON_VARIANTS[name]
})

const hasVariant = (name: string): name is EditorAddOnVariantName =>
  EDITOR_ADD_ON_VARIANTS[name as EditorAddOnVariantName] !== undefined
</script>

<style lang="scss" scoped>
.add-on-footer {
  min-height: token('font/caption/regular/line-height') * 2;
}

.price-per-line {
  white-space: nowrap;
}
</style>
